import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import * as siteDefaults from "@/config/site";

const SettingsContext = createContext(null);

// Fallback settings shape (mirrors backend settings_defaults.py) sourced from
// the local site.js file so the app renders correctly before/without a fetch.
const FALLBACK = {
  company: siteDefaults.company,
  contact: siteDefaults.contact,
  socials: siteDefaults.socials,
  homepage: {
    heroEyebrow: "Global Sourcing • International Trading • Logistics",
    heroTitle: "Worldwide sourcing, trading & logistics — coordinated by one team.",
    heroSubtitle:
      "Send your product specifications, quantity and destination. Our international trade team reviews the requirement and coordinates suitable suppliers, commercial terms and transport options.",
    heroPrimaryCta: "Request a sourcing quote",
    heroSecondaryCta: "Explore our services",
  },
  seo: {},
  services: siteDefaults.services,
  markets: siteDefaults.markets,
  industries: siteDefaults.industries,
  faqs: siteDefaults.faqs,
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({ ...FALLBACK, ...data });
    } catch (e) {
      // keep fallback
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  return ctx?.settings || FALLBACK;
};

export const useSettingsCtx = () => useContext(SettingsContext);
